import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation, forwardRef, inject, input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SearchService } from '@ukam/core/services/search.service';

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSearchInputComponent),
      multi: true
    }
  ],
  imports: [
    FormsModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    TranslocoModule
  ]
})
export class AppSearchInputComponent implements ControlValueAccessor, OnInit {
  @ViewChild('searchInput') private _searchInput!: ElementRef<HTMLInputElement>;
  private _translocoService = inject(TranslocoService);

  private _inputDebounce = new Subject<string>();
  private _touched = false;

  @Input() placeholder: string | null = this._translocoService.translate('Search');
  @Input() enableClear = true;
  readonly working = input(false);
  @Input() debounceMs = 500;
  @Input() searchService!: SearchService<any>;


  @Output() update = new EventEmitter<string>();

  value: string | null = null;
  disabled = false;
  onChanged: (v: string | null) => void = () => { };
  onTouched: () => void = () => { };

  ngOnInit(): void {
    this._inputDebounce.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      filter(x => x.length > 0 || this._touched)
    ).subscribe(() => {
      this._touched = true;
      this.setUpdate();
    });
  }

  ngAfterViewInit(): void {
    this._searchInput?.nativeElement?.focus();
  }

  setUpdate(): void {
    this.setChange();
    this.update.emit(this.value ?? '');
  }

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  setChange(): void {
    if (this.disabled) return;
    this.onChanged(this.value);
    this.onTouched();
  }

  setBlur(): void {
    this.onTouched();
  }

  onKeyUp(): void {
    this._inputDebounce.next(this.value ?? '');
  }

  clear(): void {
    this.value = null;
    this.setChange();
    this.setUpdate();
    this._searchInput?.nativeElement?.focus();
  }
}
